import * as Blockly from "blockly";


// ============================================================
// Zero Robotics C++ Generator
// ============================================================
//
// This is a lightweight custom generator for the legacy
// Zero Robotics Blockly blocks.
//
// It intentionally does NOT extend Blockly.Generator.
// This avoids compatibility problems between the legacy
// Zero Robotics generator and modern Blockly.
//

class ZrCppGenerator {

    constructor() {

        // ----------------------------------------------------
        // Generator name
        // ----------------------------------------------------

        this.name_ = "zr_cpp";


        // ----------------------------------------------------
        // Block generators
        // ----------------------------------------------------

        this.forBlock = Object.create(null);


        // ----------------------------------------------------
        // Blockly operator precedence
        //
        // These are the standard Blockly precedence values.
        // ----------------------------------------------------

        this.ORDER_ATOMIC = 0;

        this.ORDER_NEW = 1;

        this.ORDER_FUNCTION_CALL = 2;

        this.ORDER_MEMBER = 3;

        this.ORDER_POSTFIX = 4;

        this.ORDER_UNARY = 5;

        this.ORDER_MULTIPLICATIVE = 6;

        this.ORDER_ADDITIVE = 7;

        this.ORDER_SHIFT = 8;

        this.ORDER_RELATIONAL = 9;

        this.ORDER_EQUALITY = 10;

        this.ORDER_BITWISE_AND = 11;

        this.ORDER_BITWISE_XOR = 12;

        this.ORDER_BITWISE_OR = 13;

        this.ORDER_LOGICAL_AND = 14;

        this.ORDER_LOGICAL_OR = 15;

        this.ORDER_CONDITIONAL = 16;

        this.ORDER_ASSIGNMENT = 17;

        this.ORDER_COMMA = 18;

        this.ORDER_NONE = 99;
    }


    // ========================================================
    // init
    // ========================================================

    init(workspace) {

        this.workspace_ = workspace;

        return this;
    }


    // ========================================================
    // blockToCode
    // ========================================================

    blockToCode(block) {

        if (!block) {
            return "";
        }


        const generator =
            this.forBlock[block.type];


        if (typeof generator !== "function") {

            throw new Error(
                'Language "zr_cpp" does not know how ' +
                `to generate code for block type "${block.type}".`
            );
        }


        return generator.call(
            this,
            block,
            this
        );
    }


    // ========================================================
    // valueToCode
    // ========================================================

    valueToCode(
        block,
        name,
        outerOrder
    ) {

        if (!block) {
            return "";
        }


        const targetBlock =
            block.getInputTargetBlock(name);


        if (!targetBlock) {
            return "";
        }


        const code =
            this.blockToCode(targetBlock);


        if (Array.isArray(code)) {

            const text = code[0];

            const innerOrder =
                typeof code[1] === "number"
                    ? code[1]
                    : this.ORDER_ATOMIC;


            if (
                typeof outerOrder === "number" &&
                innerOrder > outerOrder
            ) {

                return "(" + text + ")";
            }


            return text;
        }


        return code || "";
    }


    // ========================================================
    // statementToCode
    // ========================================================

    statementToCode(
        block,
        name
    ) {

        if (!block) {
            return "";
        }


        const firstBlock =
            block.getInputTargetBlock(name);


        if (!firstBlock) {
            return "";
        }


        let code = "";

        let currentBlock =
            firstBlock;


        while (currentBlock) {

            code +=
                this.blockToCode(
                    currentBlock
                );


            currentBlock =
                currentBlock.getNextBlock();
        }


        return code;
    }


    // ========================================================
    // workspaceToCode
    // ========================================================

    workspaceToCode(workspace) {

        if (!workspace) {
            return "";
        }


        this.init(workspace);


        let code = "";


        const topBlocks =
            workspace.getTopBlocks(true);


        for (
            let i = 0;
            i < topBlocks.length;
            i++
        ) {

            const block =
                topBlocks[i];


            const blockCode =
                this.blockToCode(block);


            if (Array.isArray(blockCode)) {

                code +=
                    blockCode[0];

            } else {

                code +=
                    blockCode || "";
            }
        }


        return this.finish(code);
    }


    // ========================================================
    // finish
    // ========================================================

    finish(code) {

        return code || "";
    }


    // ========================================================
    // addReservedWords
    // ========================================================

    addReservedWords(words) {

        this.reservedWords_ =
            words
                ? words
                    .split(",")
                    .map(
                        word =>
                            word.trim()
                    )
                    .filter(Boolean)
                : [];
    }
}


// ============================================================
// Export singleton
// ============================================================

export const zrCppGenerator =
    new ZrCppGenerator();